import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Download, User } from "lucide-react";
import { UserAnalytics } from "@/hooks/useAnalytics";
import { exportToCSV, exportToJSON, shortenWallet } from "@/lib/exportUtils";
import { priceToUsd, formatUsdPrice } from "@/lib/priceUtils";
import { useSolPrice } from "@/hooks/useSolPrice";

interface UserAnalyticsTableProps {
  data: UserAnalytics[];
  title?: string;
}

export function UserAnalyticsTable({
  data,
  title = "User Statistics",
}: UserAnalyticsTableProps) {
  const [search, setSearch] = useState("");
  const { usdToSol } = useSolPrice();

  // Format price: USD primary, SOL secondary
  const formatPriceDisplay = (value: number) => {
    const usdValue = priceToUsd(value);
    const solValue = usdToSol(usdValue);
    return {
      usd: formatUsdPrice(usdValue),
      sol: `≈ ${solValue.toFixed(4)} SOL`
    };
  };

  const filteredData = useMemo(() => {
    return data.filter(
      (user) =>
        user.wallet_address.toLowerCase().includes(search.toLowerCase()) ||
        (user.display_name?.toLowerCase() || "").includes(search.toLowerCase())
    );
  }, [data, search]);

  const handleExport = (format: "csv" | "json") => {
    const exportData = filteredData.map((u) => ({
      wallet_address: u.wallet_address,
      display_name: u.display_name || "",
      role: u.role,
      nfts_owned: u.nfts_owned,
      total_nft_sales: u.total_nft_sales,
      total_nft_earned_usd: priceToUsd(u.total_nft_earned),
      total_earned_usd: priceToUsd(u.total_earned),
      withdrawn_usd: priceToUsd(u.withdrawn),
      available_balance_usd: priceToUsd(u.available_balance),
      is_banned: u.is_banned ? "Yes" : "No",
      joined_at: u.joined_at,
    }));

    if (format === "csv") {
      exportToCSV(exportData, "user-analytics");
    } else {
      exportToJSON(exportData, "user-analytics");
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-lg">{title}</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select onValueChange={(v) => handleExport(v as "csv" | "json")}>
              <SelectTrigger className="w-auto">
                <Download className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Export" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">Export CSV</SelectItem>
                <SelectItem value="json">Export JSON</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>NFTs</TableHead>
                <TableHead>Sales</TableHead>
                <TableHead>NFT Earnings</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Withdrawn</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((user) => {
                  const earnedDisplay = formatPriceDisplay(user.total_nft_earned);
                  const balanceDisplay = formatPriceDisplay(user.available_balance);
                  const withdrawnDisplay = formatPriceDisplay(user.withdrawn);
                  
                  return (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={user.avatar_url || undefined} />
                            <AvatarFallback>
                              <User className="w-4 h-4" />
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm">
                              {user.display_name || shortenWallet(user.wallet_address)}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {shortenWallet(user.wallet_address)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.role}</Badge>
                      </TableCell>
                      <TableCell>{user.nfts_owned}</TableCell>
                      <TableCell>{user.total_nft_sales}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-green-500 font-medium">{earnedDisplay.usd}</span>
                          <span className="text-xs text-muted-foreground">{earnedDisplay.sol}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{balanceDisplay.usd}</span>
                          <span className="text-xs text-muted-foreground">{balanceDisplay.sol}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-muted-foreground">{withdrawnDisplay.usd}</span>
                          <span className="text-xs text-muted-foreground">{withdrawnDisplay.sol}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_banned ? "destructive" : "secondary"}>
                          {user.is_banned ? "Banned" : "Active"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 text-xs text-muted-foreground">
          Showing {filteredData.length} of {data.length} users
        </div>
      </CardContent>
    </Card>
  );
}
